/**
 * CommentPreview Component
 * Feature: 055-my-critiques-page
 *
 * Displays a preview of a critique comment with username, text, and timestamp.
 * Used in CritiqueSessionListItem to show recent comments.
 * Shares rendering logic with CritiqueCard for consistent styling.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CommentPreview as CommentPreviewType } from '../../../types/critique-session.types';
import type { MarkupEditData } from '../../../types/markup.types';
import { MarkdownRenderer } from '../../MarkdownRenderer';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';
import { MarkupPreviewModal } from '../../modals/MarkupPreviewModal';
import { formatRelativeTime } from '../../../utils/time';
import { critiqueUrl } from '../../../utils/urls';
import { parseCritiqueContent } from '../../../utils/critiqueContentParser';
import logger from '../../../utils/logger';

export interface CommentPreviewProps {
  comment: CommentPreviewType;
  sessionId: string; // Critique session global ID
  sessionSlug: string; // Session slug for URL
}

/**
 * Comment preview component
 *
 * Displays username and comment text with CSS-based truncation.
 * Note: Do NOT truncate HTML strings with substring() - it breaks HTML tags!
 * Use CSS line-clamp or max-height instead.
 *
 * Styling matches CritiqueCard's compact layout for visual consistency.
 *
 * @param comment - Comment preview data
 *
 * @example
 * ```tsx
 * <CommentPreview comment={{
 *   globalId: 'cr5x',
 *   username: 'photographer123',
 *   text: 'Love the composition!',
 *   createdAt: '2025-11-14T11:45:00Z'
 * }} />
 * ```
 */
export function CommentPreview({ comment, sessionId, sessionSlug }: CommentPreviewProps) {
  const navigate = useNavigate();
  const relativeTime = formatRelativeTime(comment.createdAt);

  // State for markup preview modal
  const [selectedMarkupIndex, setSelectedMarkupIndex] = useState<number | null>(null);

  // Strip markdown blocks (```markup, ```edit) from comment text for preview display
  const cleanedText = useMemo(() => {
    return parseCritiqueContent(comment.text).cleanedContent;
  }, [comment.text]);

  // Handle comment click - navigate to critique session with photo and comment query params
  const handleCommentClick = () => {
    const baseUrl = critiqueUrl(sessionId, sessionSlug);
    const url = `${baseUrl}?photo=${comment.photo.id}&comment=${comment.globalId}`;

    logger.info('CommentPreview', 'Navigating to comment', {
      sessionId,
      photoId: comment.photo.id,
      commentId: comment.globalId,
      url,
    });

    navigate(url);
  };

  // Handle shape reference click - open preview modal for specific shape
  const handleShapeReferenceClick = (shapeNumber: number) => {
    if (!comment.markup || comment.markup.length === 0) return;

    // Find the markup that contains the referenced shape
    const markupIndex = comment.markup.findIndex((m) => {
      // Type guard: check if editData has shapes property (markup edit)
      const editData = m.editData as unknown as MarkupEditData;
      const shapes = editData?.shapes || [];
      return shapes.some((s) => s.number === shapeNumber);
    });

    if (markupIndex >= 0) {
      logger.info('CommentPreview', 'Opening markup preview for shape:', shapeNumber);
      setSelectedMarkupIndex(markupIndex);
    }
  };

  return (
    <div
      className="rounded border border-border bg-surface-2 p-2 flex gap-3 cursor-pointer hover:bg-surface-3 transition-colors"
      onClick={(e) => {
        e.stopPropagation(); // Prevent parent card navigation
        handleCommentClick();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          handleCommentClick();
        }
      }}
    >
      {/* Photo thumbnail */}
      <div className="flex-shrink-0 w-16 h-16">
        <AdaptivePhoto
          photo={comment.photo}
          alt={`Photo for ${comment.username}'s comment`}
          className="rounded overflow-hidden w-full h-full"
          imgClassName="w-full h-full object-cover"
        />
      </div>

      {/* Comment content */}
      <div className="flex-grow min-w-0">
        {/* User info row */}
        <div className="flex items-center gap-1 text-sm mb-1">
          <span className="font-medium text-foreground">{comment.username}</span>
          <span className="text-muted">•</span>
          <span className="text-muted">{relativeTime}</span>
        </div>

        {/* Comment content with markdown support - Feature 050: Shape references */}
        {/* Use CSS line-clamp for visual truncation without breaking HTML */}
        <div className="line-clamp-3">
          <MarkdownRenderer
            content={cleanedText}
            className="text-foreground text-sm leading-snug break-words"
            photo={comment.photo}
            markup={comment.markup}
            onShapeReferenceClick={handleShapeReferenceClick}
          />
        </div>
      </div>

      {/* Feature 050: Markup Preview Modal */}
      {selectedMarkupIndex !== null && comment.markup && comment.markup[selectedMarkupIndex] && (
        <MarkupPreviewModal
          isOpen={true}
          onClose={() => {
            logger.info('CommentPreview', 'Closing markup preview modal');
            setSelectedMarkupIndex(null);
          }}
          photo={comment.photo}
          shapes={(comment.markup[selectedMarkupIndex].editData as unknown as MarkupEditData)?.shapes || []}
        />
      )}
    </div>
  );
}
